// Image slot bound to a form path (SPEC §D.6): the About-page field plus a
// size limit checked before upload, a real thumbnail, Replace, clear ✕ and a
// busy spinner. Never UploadSingleFile (it shows a static success picture).
// Uploads go to /files/media; the response is not wrapped in {data}.
import { useContext, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorText } from 'src/utils/apiErrorText';
import { fieldDomId, LandingEditorContext } from '../LandingEditorContext';
import { formatBytes } from '../rules/format';

type Props = {
  name: string;
  label: string;
  hint?: string;
  maxBytes?: number;
  // e.g. 'image/png,image/webp' for tool logos (/files/media stores no ContentType).
  accept?: string;
  // Thumbnail shape; 16/10 for covers, 1 for photos and logos.
  aspect?: number;
  required?: boolean;
};

const MB = 1024 * 1024;

export default function ImageField({
  name,
  label,
  hint,
  maxBytes = 2 * MB,
  accept = 'image/jpeg,image/png,image/webp',
  aspect = 16 / 10,
  required,
}: Props) {
  const { control } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();
  // Outside the landing editor (the core diploma modal) there is no upload
  // counter holding Save; the field still shows its busy state.
  const ctx = useContext(LandingEditorContext);
  const beginUpload = ctx ? ctx.beginUpload : () => () => undefined;
  const [busy, setBusy] = useState(false);
  const thumbH = 72;
  const thumbW = Math.round(thumbH * aspect);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const value: string = field.value || '';
        const upload = async (file?: File) => {
          if (!file) return;
          const allowed = accept.split(',').map((t) => t.trim());
          if (allowed.indexOf(file.type) < 0) {
            enqueueSnackbar(`This file type (${file.type || 'unknown'}) is not allowed here.`, {
              variant: 'error',
            });
            return;
          }
          if (file.size > maxBytes) {
            enqueueSnackbar(
              `This image is ${formatBytes(file.size)}; the limit is ${formatBytes(maxBytes)}.`,
              { variant: 'error' }
            );
            return;
          }
          const done = beginUpload();
          setBusy(true);
          try {
            const form = new FormData();
            form.append('file', file);
            const res = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
            const url = res.data?.url ?? res.data?.data?.url;
            if (!url) throw new Error('The upload returned no URL');
            field.onChange(url);
          } catch (e: any) {
            enqueueSnackbar(apiErrorText(e, 'Upload failed'), { variant: 'error' });
          } finally {
            setBusy(false);
            done();
          }
        };

        return (
          <Stack spacing={0.75} id={fieldDomId(name)} sx={{ scrollMarginTop: 120 }}>
            <Typography variant="subtitle2">
              {label}
              {required && (
                <Typography component="span" color="error.main">
                  {' '}
                  *
                </Typography>
              )}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: thumbW,
                  height: thumbH,
                  flexShrink: 0,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: error ? 'error.main' : 'divider',
                  bgcolor: 'background.neutral',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {value ? (
                  <Box
                    component="img"
                    src={value}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Iconify icon="eva:image-outline" sx={{ color: 'text.disabled', width: 24, height: 24 }} />
                )}
              </Box>
              <Button
                component="label"
                size="small"
                variant="outlined"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} /> : <Iconify icon="eva:cloud-upload-outline" />}
              >
                {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
                <input
                  hidden
                  type="file"
                  accept={accept}
                  onChange={(e) => {
                    upload(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </Button>
              {value && (
                <Tooltip title="Open the image">
                  <IconButton size="small" component="a" href={value} target="_blank" rel="noopener noreferrer">
                    <Iconify icon="eva:external-link-outline" />
                  </IconButton>
                </Tooltip>
              )}
              {value && (
                <Tooltip title="Remove the image">
                  <IconButton size="small" color="error" onClick={() => field.onChange('')}>
                    <Iconify icon="eva:close-outline" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'}>
              {error?.message ||
                [hint, `Up to ${formatBytes(maxBytes)}`, accept.replace(/image\//g, '').toUpperCase()]
                  .filter(Boolean)
                  .join(' · ')}
            </Typography>
          </Stack>
        );
      }}
    />
  );
}
